import { getProfilePictureUrl } from '@internal/services/TSalesManager';
import { stopPropagation } from '@internal/utils/events/stopPropagation';
import {
    CmsMedia,
    CmsMediaFile,
    isCmsMedia,
    isCmsMediaFile,
    LocalMedia,
    MachineMedia,
    Media as MediaType,
} from '@internal/utils/media/Media';
import {
    getMediaSrc,
    getMediaUrl,
    ImageSizeSet,
} from '@internal/utils/media/getMediaSrc';
import { RatioContainer } from '@renderer-ui-library/atoms';
import classnames, { default as classNames } from 'classnames';
import { useMemo } from 'react';
import { SetNonNullable } from 'type-fest';
import { ImageSlider } from '../ImageSlider';
import styles from './media.module.scss';
import { DynamicYoutubeVideo } from './youtube/dyanmicYouTubeVideo';

type DisplayRatio = 'original' | number;

type Props = {
    media: MediaType | CmsMedia | MachineMedia;
    ratio?: DisplayRatio;
    autoPlayVideo?: boolean;
    autoPlaySlideshow?: boolean;
    cover?: boolean;
    loading?: 'lazy' | 'eager' | undefined;
    sizeSet?: ImageSizeSet;
    width?: number;
    height?: number;
};
export const Media: React.FC<Props> = ({
    media,
    ratio,
    autoPlayVideo,
    autoPlaySlideshow,
    cover,
    loading = 'lazy',
    sizeSet,
    height,
    width,
}) => {
    const mediaData:
        | {
              type: 'image';
              media: CmsMediaFile | LocalMedia | MachineMedia;
              displayRatio?: DisplayRatio;
              caption?: string;
          }
        | {
              type: 'profile-picture';
              url: string;
              displayRatio?: DisplayRatio;
              alt: string;
              timestamp: string | null;
          }
        | {
              type: 'video';
              media: SetNonNullable<CmsMedia, 'videoUrl'>;
              displayRatio?: DisplayRatio;
          }
        | { type: 'images'; media: CmsMediaFile[]; displayRatio?: DisplayRatio }
        | null = useMemo(() => {
        if (isCmsMediaFile(media)) {
            return {
                type: 'image',
                media,
                imageCaption: media.attributes.caption,
                displayRatio: ratio,
            };
        }
        if (isCmsMedia(media)) {
            if (media.videoUrl) {
                return {
                    type: 'video',
                    media: {
                        ...media,
                        // for some reason TS need this line to be pleased
                        videoUrl: media.videoUrl ?? '',
                    },
                    displayRatio: ratio === 'original' ? ratio : 16 / 9,
                };
            } else if (media.files.data?.length === 1) {
                return {
                    type: 'image',
                    media: media.files.data[0],
                    caption: media.imageCaption,
                    displayRatio: ratio,
                };
            } else if (media.files.data && media.files.data.length > 1) {
                return {
                    type: 'images',
                    media: media.files.data,
                    displayRatio: ratio,
                    caption: media.imageCaption,
                };
            } else {
                return null;
            }
        } else if (media.type === 'local') {
            return {
                type: 'image',
                media,
                displayRatio: ratio,
            };
        } else if (media.type === 'machine') {
            return {
                type: 'image',
                media,
                displayRatio: ratio,
            };
        } else if (media.type === 'profile-picture') {
            return {
                type: 'profile-picture',
                url: getProfilePictureUrl(media),
                alt: media.name,
                displayRatio: ratio,
                timestamp: media.timestamp,
            };
        } else {
            return null;
        }
    }, [media, ratio]);

    const mediaWrapper = useMemo(() => {
        if (!mediaData) {
            return null;
        }
        if (mediaData.type === 'video') {
            return (
                <div className={styles.wrapper}>
                    <DynamicYoutubeVideo
                        url={mediaData.media.videoUrl}
                        autoPlayVideo={autoPlayVideo}
                        className={classNames(styles.video, {
                            [styles.videoCoverMode]:
                                mediaData.displayRatio === 'original',
                        })}
                    />
                </div>
            );
        }

        if (mediaData.type === 'images') {
            return (
                <ImageSlider
                    images={mediaData.media}
                    ratio={
                        (mediaData.displayRatio ?? (width && height))
                            ? width! / height!
                            : 1
                    }
                    autoPlay={autoPlaySlideshow}
                    pagination={!autoPlaySlideshow}
                    arrows={false}
                    sizeSet={sizeSet}
                />
            );
        }

        if (mediaData.type === 'profile-picture') {
            return (
                <div className={styles.wrapper} onClick={stopPropagation}>
                    <div>
                        <figure>
                            <img
                                loading={loading}
                                className={classnames(styles.media, {
                                    [styles.cover]: cover,
                                })}
                                alt={mediaData.alt}
                                src={mediaData.url}
                            />
                        </figure>
                    </div>
                </div>
            );
        }

        if (mediaData.type === 'image') {
            const image = mediaData.media;
            let srcAttributes;

            if (width || height) {
                srcAttributes = {
                    src: getMediaUrl(image, {
                        width,
                        height,
                    }),
                };
            } else if (mediaData.displayRatio) {
                srcAttributes = getMediaSrc(image, {
                    ratio: mediaData.displayRatio,
                    sizeSet,
                });
            } else {
                srcAttributes = getMediaSrc(image, {
                    sizeSet,
                });
            }

            return (
                <div className={styles.wrapper} onClick={stopPropagation}>
                    <div>
                        <figure>
                            <img
                                loading={loading}
                                className={classnames(styles.media, {
                                    [styles.cover]: cover,
                                })}
                                alt={
                                    isCmsMediaFile(image)
                                        ? image.attributes.alternativeText
                                        : ''
                                }
                                {...srcAttributes}
                                width={width}
                                height={height}
                            />
                        </figure>
                        {mediaData.caption && (
                            <figcaption className={styles.figCaption}>
                                {mediaData.caption}
                            </figcaption>
                        )}
                    </div>
                </div>
            );
        }
    }, [
        autoPlaySlideshow,
        autoPlayVideo,
        cover,
        height,
        loading,
        mediaData,
        sizeSet,
        width,
    ]);

    if (!mediaWrapper) {
        return null;
    }

    if (
        mediaData?.displayRatio === 'original' ||
        (mediaData?.type === 'image' && width && height) ||
        mediaData?.type === 'images'
    ) {
        return mediaWrapper;
    }

    return (
        <RatioContainer
            ratio={
                mediaData?.displayRatio
                    ? mediaData?.displayRatio
                    : typeof ratio === 'number'
                      ? ratio
                      : width && height
                        ? { width, height }
                        : 1
            }
            flex
        >
            {mediaWrapper}
        </RatioContainer>
    );
};
